import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import { buildDeleteQueryString } from '@/lib/deleteOptions';
import type { AnalysisSetState } from './useDocuments';

export interface Folder {
  folder_id: number;
  guid: string;
  name: string;
  parent_folder_id?: number;
  matter_id: number;
  path: string;
  depth: number;
  is_active: boolean;
  analysis_set_state?: AnalysisSetState;
  analysis_set_effective?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface FolderTree extends Folder {
  children: FolderTree[];
  document_count: number;
  token_count: number;
  analysis_set_addable_document_count?: number;
  analysis_set_addable_token_count?: number;
  is_synced?: boolean;  // True if folder contains synced content (restricts rename/move/delete)
  is_sync_root?: boolean;  // True if folder is a root sync destination (shows indicator, allows rename/move)
  sync_providers?: string[];  // List of provider types syncing to this folder (e.g., ['imanage', 'outlook'])
}

export interface FolderCreate {
  name: string;
  parent_folder_id?: number;
}

export interface FolderUpdate {
  name?: string;
}

export interface FolderMove {
  parent_folder_id?: number;
}

export type UseFolderTreeOptions = {
  analysisSetOnly?: boolean;
};

export const findFolderInTree = (nodes: FolderTree[], targetGuid: string): FolderTree | null => {
  for (const node of nodes) {
    if (node.guid === targetGuid) {
      return node;
    }
    if (node.children?.length) {
      const match = findFolderInTree(node.children, targetGuid);
      if (match) {
        return match;
      }
    }
  }
  return null;
};

export const useFolders = (matterSlug: string) => {
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    matterSlug ? `/matter/${matterSlug}/folders` : null,
    (url) => backendClient.get(url).then(res => res.data)
  );

  return {
    folders: data as Folder[] || [],
    isLoading,
    isValidating,
    error,
    mutate
  };
};

export const useFolderTree = (matterSlug: string, options: UseFolderTreeOptions = {}) => {
  const params = new URLSearchParams();
  if (options.analysisSetOnly) {
    params.append('analysis_set', 'active');
  }
  const queryParams = params.toString() ? `?${params.toString()}` : '';

  const { data, error, isLoading, mutate } = useSWR(
    matterSlug ? `/matter/${matterSlug}/folders/tree${queryParams}` : null,
    (url) => backendClient.get(url).then(res => res.data)
  );

  return {
    folderTree: data as FolderTree[] || [],
    isLoading,
    error,
    mutate
  };
};

export const createFolder = async (matterSlug: string, folderData: FolderCreate): Promise<Folder> => {
  const response = await backendClient.post(`/matter/${matterSlug}/folders`, folderData);
  return response.data;
};

export const updateFolder = async (matterSlug: string, folderGuid: string, folderData: FolderUpdate): Promise<Folder> => {
  const response = await backendClient.put(`/matter/${matterSlug}/folders/${folderGuid}`, folderData);
  return response.data;
};

export const deleteFolder = async (
  matterSlug: string,
  folderGuid: string,
  recursive: boolean = false,
  force: boolean = false,
  deleteAttachments: boolean = false,
): Promise<void> => {
  const queryString = buildDeleteQueryString({
    recursive,
    force,
    deleteAttachments,
  });
  await backendClient.delete(`/matter/${matterSlug}/folders/${folderGuid}${queryString}`);
};

export const moveFolder = async (matterSlug: string, folderGuid: string, moveData: FolderMove): Promise<void> => {
  await backendClient.post(`/matter/${matterSlug}/folders/${folderGuid}/move`, moveData);
};
